import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Params } from "@angular/router";
import { Observable } from "rxjs";
import { DadosNeshRepresentation } from "src/app/core/models/nesh-dados.model";
import { NeshImagemRepresentation } from "src/app/core/models/nesh-imagem.model";
import { NotaNeshRepresentation } from "src/app/core/models/nesh.model";
import { NotaRepresentation } from "src/app/core/models/nota.model";
import { BuscaNomencalturaRepresentation } from "src/app/core/models/busca-nomenclatura.model";
import { environment } from "src/environments/environment";
import { TIPO_NOTA } from "./nesh.constants";
import { NeshConsulta } from "src/app/core/models/nesh-consulta.model";

@Injectable({
  providedIn: "root",
})
export class NeshService {
  private http = inject(HttpClient);
  readonly apiUrl = `${environment.appContext}/api/nesh`;

  getNotaListByCodOrigemSecao(
    listaCodSecao: string[],
    dataInteresse: string,
  ): Observable<NotaRepresentation[]> {
    const tipo = TIPO_NOTA.secao.codigo; /*NS = Nota de Seção*/
    let parametros: HttpParams = new HttpParams()
      .append("dataInteresse", dataInteresse)
      .append("tipoNota", tipo);

    listaCodSecao.forEach((item: string) => {
      parametros = parametros.append("cod", item);
    });

    const url = `${this.apiUrl}/teste`;
    return this.http.get<NotaRepresentation[]>(url, {
      params: parametros,
    });
  }

  getNotaListByCodCapitulo(
    listaCodCapitulo: string[],
    dataInteresse: string,
  ): Observable<NotaRepresentation[]> {
    const tipo = TIPO_NOTA.capitulo.codigo; /*NC = Nota de Capítulo*/
    let parametros: HttpParams = new HttpParams()
      .append("dataInteresse", dataInteresse)
      .append("tipoNota", tipo);

    listaCodCapitulo.forEach((item: string) => {
      parametros = parametros.append("cod", item);
    });

    const url = `${this.apiUrl}/teste`;
    return this.http.get<NotaRepresentation[]>(url, {
      params: parametros,
    });
  }

  getNeshListPesquisa(
    filtro: any,
  ): Observable<BuscaNomencalturaRepresentation> {
    const url = `${this.apiUrl}/pesquisa`;
    const parametroPlataforma: HttpParams = new HttpParams().append(
      "nao-mostrar-aguarde",
      true,
    );
    const options = {
      params: parametroPlataforma,
    };
    return this.http.post<BuscaNomencalturaRepresentation>(
      url,
      filtro,
      options,
    );
  }

  getSecaoNcmList(queryParams: Params): Observable<NotaRepresentation[]> {
    const parametros: HttpParams = new HttpParams()
      .append("id", queryParams.id.replace(/\D/g, ""))
      .append("tipoNota", queryParams.tipoNota)
      .append("dataPesquisa", queryParams.dataPesquisa);

    return this.http.get<NotaRepresentation[]>(this.apiUrl, {
      params: parametros,
    });
  }

  getNotaNesh(filtro: { id: string; tipoNota: string; dataPesquisa: string }) {
    const idSecaoNcm: string = filtro.id;
    const parametros: HttpParams = new HttpParams()
      .append("idSecaoNcm", filtro.id)
      .append("tipoNota", filtro.tipoNota)
      .append("dataPesquisa", filtro.dataPesquisa);

    const url = `${this.apiUrl}/navegacao/${idSecaoNcm}`;

    return this.http.get<NotaNeshRepresentation>(url, {
      params: parametros,
    });
  }

  getNotaByNavegacao(filtro: {
    id: string;
    tipoNota: string;
    dataPesquisa: string;
    direcao: string;
  }) {
    const idSecaoNcm: string = filtro.id;
    const direcao: string = filtro.direcao;
    const parametros: HttpParams = new HttpParams()
      .append("idSecaoNcm", filtro.id)
      .append("tipoNota", filtro.tipoNota)
      .append("dataPesquisa", filtro.dataPesquisa);

    const url = `${this.apiUrl}/navegacao/${idSecaoNcm}/${direcao}`;

    return this.http.get<NotaNeshRepresentation>(url, {
      params: parametros,
    });
  }

  getNeshPorDocumento(filtro: { codDocumento: string; dataPesquisa: string }) {
    const codDocumento: string = filtro.codDocumento;
    const parametros: HttpParams = new HttpParams().append(
      "dataPesquisa",
      filtro.dataPesquisa,
    );
    const url = `${this.apiUrl}/documento/${codDocumento}`;
    return this.http.get<NotaNeshRepresentation>(url, {
      params: parametros,
    });
  }

  getIdSecao(dataInteresse: Date): Observable<NotaRepresentation> {
    const url = `${this.apiUrl}/neshSecao`;
    const parametros: HttpParams = new HttpParams().append(
      "dataInteresse",
      dataInteresse.toISOString(),
    );
    return this.http.get<NotaRepresentation>(url, {
      params: parametros,
    });
  }

  getIdRGI(dataInteresse: Date): Observable<NotaRepresentation> {
    const url = `${this.apiUrl}/rgi`;
    const parametros: HttpParams = new HttpParams().append(
      "dataInteresse",
      dataInteresse.toISOString(),
    );
    return this.http.get<NotaRepresentation>(url, { params: parametros });
  }

  download(filtro: any): Observable<HttpResponse<Blob>> {
    let parametros = new HttpParams();
    Object.keys(filtro).forEach(function (item) {
      parametros = parametros.append(item, filtro[item]);
    });

    const requestOptions: Object = {
      observe: "response",
      responseType: "blob",
      params: parametros,
    };

    const url = `${this.apiUrl}/arquivo/download`;
    return this.http.get<HttpResponse<Blob>>(url, requestOptions);
  }

  getImagens(): Observable<NeshImagemRepresentation[]> {
    const url = `${this.apiUrl}/imagens`;
    return this.http.get<NeshImagemRepresentation[]>(url);
  }

  excluirImagem(id: number): Observable<any> {
    const url = `${this.apiUrl}/imagens`;
    return this.http.delete(`${url}/${id}`);
  }

  importarImagem(file: File): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", encodeURIComponent(file.name));
    const url = `${this.apiUrl}/imagens/salvar`;
    return this.http.post<any>(url, formData);
  }

  importar(
    file: File,
    acao: string,
    novaEmenda: string,
  ): Observable<DadosNeshRepresentation[]> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", encodeURIComponent(file.name));
    formData.append("acao", acao);
    formData.append("novaEmenda", novaEmenda);
    const url = `${this.apiUrl}/upload`;
    return this.http.post<DadosNeshRepresentation[]>(url, formData);
  }

  getNeshById(queryParams: Params): Observable<NeshConsulta> {
    const idNesh: number = queryParams.id;
    const parametros: HttpParams = new HttpParams()
      .append("dataPesquisa", queryParams.dataPesquisa)
      .append("tipoNota", queryParams.tipoNota);

    const url = `${this.apiUrl}/pesquisar-icone/${idNesh}`;
    return this.http.get<NeshConsulta>(url, {
      params: parametros,
    });
  }

  getNeshByCodigo(queryParams: Params): Observable<NeshConsulta> {
    const codigoNcm: string = queryParams.codigoNcm;
    const parametros: HttpParams = new HttpParams().append(
      "dataPesquisa",
      queryParams.dataPesquisa,
    );
    const url = `${this.apiUrl}/pesquisar/${codigoNcm}`;
    return this.http.get<NeshConsulta>(url, {
      params: parametros,
    });
  }

  getAnexo(queryParams: Params): Observable<any> {
    const codigoAnexo: string = queryParams.codigoAnexo;
    const params: HttpParams = new HttpParams().append(
      "dataPesquisa",
      queryParams.dataPesquisa,
    );
    const url = `${this.apiUrl}/documento/${codigoAnexo}`;
    return this.http.get<any>(url, { params });
  }
}
